import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar,
  Clock,
  User,
  Mail,
  FileText,
  CheckCircle,
  Loader2,
  ArrowRight,
  Building2,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const leadId = searchParams.get("lead_id");

  const [lead, setLead] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [leadId]);

  const fetchData = async () => {
    try {
      if (leadId) {
        const leadRes = await axios.get(`${API}/leads/${leadId}`);
        setLead(leadRes.data);
      }
      const availRes = await axios.get(`${API}/availability`);
      setAvailability(availRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupedAvailability = availability.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSlot || !formData.name || !formData.email) {
      toast.error("Please fill in all required fields and select a time slot");
      return;
    }

    setSubmitting(true);
    try {
      const appointmentDateTime = `${selectedSlot.date}T${selectedSlot.start_time}:00`;
      
      await axios.post(`${API}/appointments`, {
        lead_id: leadId || `guest-${Date.now()}`,
        name: formData.name,
        email: formData.email,
        appointment_datetime: appointmentDateTime,
        notes: formData.notes || null
      });

      setSuccess(true);
      toast.success("Appointment booked successfully!");
    } catch (error) {
      console.error("Failed to book appointment:", error);
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background" data-testid="booking-success">
        <div className="booking-container">
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Booking Confirmed!</h1>
              <p className="text-muted-foreground mb-6">
                We've received your booking request. You'll receive a confirmation email shortly.
              </p>
              <div className="bg-muted rounded-lg p-4 inline-block text-left">
                <p className="text-sm"><strong>Date:</strong> {selectedSlot?.date}</p>
                <p className="text-sm"><strong>Time:</strong> {selectedSlot?.start_time} - {selectedSlot?.end_time}</p>
                <p className="text-sm"><strong>Name:</strong> {formData.name}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="booking-page">
      {/* Header */}
      <div className="bg-primary text-primary-foreground py-8">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Zap size={24} />
            </div>
            <span className="text-xl font-bold">LeadForge</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">Book Your Website Consultation</h1>
          <p className="text-primary-foreground/80">
            Select a convenient time to discuss your new website
          </p>
        </div>
      </div>

      <div className="booking-container">
        {/* Lead Info (if available) */}
        {lead && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">{lead.business_name}</p>
                  <p className="text-sm text-muted-foreground">{lead.city}, {lead.state}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Date & Time Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Calendar size={20} className="text-accent" />
                  Select Date & Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Date Selection */}
                  <div>
                    <Label className="mb-3 block">Choose a Date</Label>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(groupedAvailability).slice(0, 7).map((date) => (
                        <button
                          key={date}
                          type="button"
                          onClick={() => {
                            setSelectedDate(date);
                            setSelectedSlot(null);
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            selectedDate === date
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-border hover:border-accent hover:bg-accent/5'
                          }`}
                          data-testid={`date-${date}`}
                        >
                          <span className="block">
                            {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                          </span>
                          <span className="text-xs opacity-80">
                            {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time Selection */}
                  {selectedDate && (
                    <div className="animate-fade-in">
                      <Label className="mb-3 block">Choose a Time</Label>
                      <div className="time-slots-grid">
                        {groupedAvailability[selectedDate]?.map((slot) => (
                          <button
                            key={slot.slot_id}
                            type="button"
                            onClick={() => setSelectedSlot(slot)}
                            className={`time-slot ${selectedSlot?.slot_id === slot.slot_id ? 'selected' : ''}`}
                            data-testid={`slot-${slot.slot_id}`}
                          >
                            <Clock size={14} className="inline mr-1" />
                            {slot.start_time}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <User size={20} className="text-accent" />
                  Your Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="form-group">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Smith"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      data-testid="notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any specific questions or topics you'd like to discuss?"
                      rows={3}
                    />
                  </div>

                  {/* Selection Summary */}
                  {selectedSlot && (
                    <div className="bg-muted rounded-lg p-4 animate-fade-in">
                      <p className="text-sm font-medium mb-2">Selected Time:</p>
                      <p className="text-sm">
                        {new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })} at {selectedSlot.start_time}
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit"
                    className="w-full"
                    disabled={!selectedSlot || !formData.name || !formData.email || submitting}
                    data-testid="submit-booking-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        Confirm Booking
                        <ArrowRight size={16} className="ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
    </div>
  );
}
